import React, {useEffect, useMemo, useRef, useState} from "react";
import {toast} from "react-toastify";
import { useTranslation } from "react-i18next";
import {createBulkQuestions, createBulkQuestionsByExamBase, getAiQuestions} from "../../../services/aiQuestionsApi";
import styles from "./AiQuestionGenerateModal.module.css";
import Select from "../../../components/common/select/Select";
import {getExamBaseById} from "../../../services/examBasesApi";
import QuestionContent from "../../../components/common/QuestionContent/QuestionContent";
import {DifficultyLevels} from "../../../utils/difficulty.util";
import {useLanguage} from "../../../context/LanguageContext";
import TopicSelector from "../../../components/common/topicSelector/TopicSelector";

const AI_LANGUAGE_OPTIONS = ["az", "en", "ru"];

const AiQuestionGenerateModal = ({isOpen, onClose, examBaseId, onSuccess, subjectOptions, onSubjectsChanged}) => {
    const { t } = useTranslation();
    const { language: uiLanguage } = useLanguage();
    const [topic, setTopic] = useState("");
    const [count, setCount] = useState(5);
    const [optionCount, setOptionCount] = useState(4);
    const [difficultyLevel, setDifficultyLevel] = useState(null);
    const [language, setLanguage] = useState(uiLanguage);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [generated, setGenerated] = useState([]);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState({});

    const [examBase, setExamBase] = useState({});

    const [subjectId, setSubjectId] = useState("");
    const [targetSubjectId, setTargetSubjectId] = useState("");
    /** Parent subject (fənn) of the chosen topic, sent to disambiguate the prompt: "Funksiyalar"
        means different things under Riyaziyyat and Fizika. Empty for a top-level subject. */
    const [subjectName, setSubjectName] = useState("");

    const previewRef = useRef(null);

    const resetForm = () => {
        setTopic("");
        setCount(5);
        setOptionCount(4);
        setDifficultyLevel(null);
        setLanguage(uiLanguage);
        setGenerated([]);
        setSelected({});
        setError("");
        setLoading(false);
        setSaving(false);
        setSubjectId("");
        setTargetSubjectId("");
        setSubjectName("");
        setExamBase({});
    };

    const handleClose = () => {
        resetForm();
        onClose?.();
    };

    useEffect(() => {
        if (!isOpen) return;
        resetForm();
        fetchExamBase();
    }, [isOpen]);

    // Once generation finishes with results, bring them into view so the user doesn't have to scroll
    // down to find the freshly generated questions. (Errors are shown at the top and handled there.)
    useEffect(() => {
        if (!loading && generated.length > 0) {
            previewRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
        }
    }, [loading, generated]);

    const fetchExamBase = () => {
        if (examBaseId) {
            getExamBaseById(examBaseId).then((response) => {
                setExamBase(response);
                const subs = response.subjects || [];
                if (subs.length > 1) {
                    // Multi-topic template: default the target subject to the primary topic.
                    setTargetSubjectId(String(subs[0].id));
                }
                // Pre-fill the editable topic with the (primary) subject's NAME — that's what drives the
                // AI prompt. Only fall back to the exam/template title when the template carries no
                // subject at all (legacy data); otherwise we'd seed the prompt with the exam name.
                setTopic(subs[0]?.title || response.subjectTitle || response.title);
            })
        }
    }

    const numCount = Math.min(10, Math.max(1, Number(count) || 1));
    const numOptionCount = Math.min(5, Math.max(2, Number(optionCount) || 2));

    const examSubjects = examBase?.subjects || [];
    const isMultiSubject = !!examBaseId && examSubjects.length > 1;

    const canGenerate = useMemo(() => {
        return (subjectId || examBaseId) && topic?.trim().length > 0 && difficultyLevel !== null && numCount > 0 && numOptionCount > 1 && (!isMultiSubject || !!targetSubjectId);
    }, [topic, difficultyLevel, numCount, numOptionCount, isMultiSubject, targetSubjectId, subjectId]);

    const selectedCount = useMemo(() => {
        if (!generated.length) return 0;
        return generated.reduce((acc, q, i) => {
            const key = q.uuid || String(i);
            return acc + (selected[key] ? 1 : 0);
        }, 0);
    }, [generated, selected]);

    const allSelected = useMemo(() => {
        return generated.length > 0 && selectedCount === generated.length;
    }, [generated.length, selectedCount]);

    const indeterminate = useMemo(() => {
        return selectedCount > 0 && selectedCount < generated.length;
    }, [selectedCount, generated.length]);

    if (!isOpen) return null;

    const handleGenerate = async ({forceNew}) => {
        if (!canGenerate) {
            setError(t("pages.aiQuestionGenerate.invalidParams"));
            return;
        }

        try {
            setError("");
            setLoading(true);

            const data = await getAiQuestions({
                topic: topic?.trim(),
                subjectName: subjectName?.trim() || undefined,
                difficultyLevel: difficultyLevel,
                count: numCount,
                optionCount: numOptionCount,
                language: language,
                examBaseId: examBaseId != null ? Number(examBaseId) : undefined,
                forceNew: forceNew,
            });

            const list = Array.isArray(data) ? data : [];
            setGenerated(list);

            const initialSelected = {};
            list.forEach((q, i) => {
                const key = q.uuid || String(i);
                initialSelected[key] = true;
            });
            setSelected(initialSelected);

            if (!list.length) {
                setError(t("pages.aiQuestionGenerate.noQuestionsReturned"));
            } else {
                toast.success(t("pages.aiQuestionGenerate.questionsCreated", { count: list.length }));
            }
        } catch (e) {
            setGenerated([]);
            setSelected({});
            // Subscription-limit errors open the global upsell modal (handled in the axios layer) — don't
            // also show an inline error here, and keep the form inputs so the user can adjust or upgrade.
            if (!e?.isSubscriptionLimit) {
                const msg =
                    e?.response?.data?.message ||
                    e?.response?.data?.error ||
                    e.message ||
                    t("pages.aiQuestionGenerate.generateError");
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateNew = () => {
        handleGenerate({forceNew: true}).then()
    }

    const toggleOne = (key) => {
        setSelected((prev) => ({...prev, [key]: !prev[key]}));
    };

    const toggleAll = () => {
        if (!generated.length) return;
        const nextValue = !(selectedCount === generated.length);

        const next = {};
        generated.forEach((q, i) => {
            const key = q.uuid || String(i);
            next[key] = nextValue;
        });
        setSelected(next);
    };

    const handleSave = async () => {
        if (!generated.length) {
            setError(t("pages.aiQuestionGenerate.noneToAdd"));
            return;
        }

        const selectedQuestions = generated.filter((q, i) => {
            const key = q.uuid || String(i);
            return !!selected[key];
        });

        if (!selectedQuestions.length) {
            setError(t("pages.aiQuestionGenerate.noneSelected"));
            return;
        }

        try {
            setError("");
            setSaving(true);
            if (examBaseId) {
                await createBulkQuestionsByExamBase(examBaseId, selectedQuestions, topic?.trim(), language, targetSubjectId || undefined)
            } else {
                await createBulkQuestions(subjectId, selectedQuestions, topic?.trim(), language)
            }
            toast.success(t("pages.aiQuestionGenerate.addSuccess"));
            // Hand back the topic these questions were filed under (question-bank flow) so the list can
            // auto-filter to it — matches manual create. The exam-base flow leaves subjectId empty and
            // its onSuccess ignores the argument.
            if (onSuccess) onSuccess(subjectId);
            handleClose();
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.aiQuestionGenerate.addError");
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const selectSubject = (id, topicObj) => {
        setSubjectId(id)
        const subject = topicObj || subjectOptions.find(s => s.id === Number(id));
        setTopic(subject?.title || "");
        setSubjectName(subject?.parentName || "");
    }

    const onSelectTargetSubject = (id) => {
        setTargetSubjectId(id);
        const subj = examSubjects.find((s) => String(s.id) === String(id));
        if (subj) {
            setTopic(subj.title || "");
            setSubjectName(subj.parentName || "");
        }
    }

    const stepCount = (delta) => setCount((c) => Math.min(10, Math.max(1, (Number(c) || 1) + delta)));
    const stepOptions = (delta) => setOptionCount((c) => Math.min(5, Math.max(2, (Number(c) || 2) + delta)));

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <header className={styles.header}>
                    <div className={styles.titleWrap}>
                        <span className={styles.titleIcon} aria-hidden>✨</span>
                        <h2 className={styles.title}>{t("pages.aiQuestionGenerate.title")}</h2>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={handleClose} aria-label={t("pages.aiQuestionGenerate.closeAria")}>
                        <span className={styles.closeIcon} aria-hidden>×</span>
                    </button>
                </header>

                <div className={styles.body}>
                    {error && (
                        <div className={styles.error} role="alert">
                            <span className={styles.errorIcon}>!</span>
                            <span>{error}</span>
                        </div>
                    )}

                    <section className={styles.section}>
                        <h3 className={styles.sectionTitle}>{t("pages.aiQuestionGenerate.parameters")}</h3>
                        <div className={styles.formCard}>
                            {
                                !examBaseId &&
                                <div className={styles.field}>
                                    <TopicSelector
                                        value={subjectId}
                                        onChange={(id, topicObj) => selectSubject(id ? String(id) : "", topicObj)}
                                        topics={subjectOptions}
                                        onTopicCreated={(newTopic) => onSubjectsChanged?.(newTopic)}
                                        placeholder={t("pages.aiQuestionGenerate.selectTopic")}
                                    />
                                </div>
                            }
                            {
                                isMultiSubject &&
                                <div className={styles.field}>
                                    <label className={styles.label} htmlFor="ai-target-subject">{t("pages.aiQuestionGenerate.targetSubject")}</label>
                                    <Select
                                        id="ai-target-subject"
                                        className={styles.filterInput}
                                        value={targetSubjectId}
                                        onChange={(e) => onSelectTargetSubject(e.target.value)}
                                        options={examSubjects.map((s) => ({
                                            value: s.id,
                                            label: s.parentName ? `${s.parentName} › ${s.title}` : s.title,
                                        }))}
                                    />
                                </div>
                            }
                            <div className={styles.field}>
                                <label className={styles.label} htmlFor="ai-topic">{t("pages.aiQuestionGenerate.topic")}</label>
                                <input
                                    id="ai-topic"
                                    className={styles.input}
                                    type="text"
                                    value={topic}
                                    onChange={(e) => setTopic(e.target.value)}
                                    autoComplete="off"
                                />
                                <p className={styles.topicHint}>
                                    <span className={styles.topicHintIcon} aria-hidden>💡</span>
                                    <span>{t("pages.aiQuestionGenerate.topicHint")}</span>
                                </p>
                            </div>

                            <div className={styles.row}>
                                <div className={styles.field}>
                                    <label className={styles.label} htmlFor="difficulty-level">{t("pages.aiQuestionGenerate.difficulty")}</label>
                                    <Select
                                        id="difficulty-level"
                                        className={styles.filterInput}
                                        value={difficultyLevel}
                                        onChange={(e) => setDifficultyLevel(e.target.value)}
                                        options={[
                                            { value: "", label: t("pages.aiQuestionGenerate.select") },
                                            ...Object.values(DifficultyLevels).map((s) => ({
                                                value: s.name,
                                                label: s.label,
                                            })),
                                        ]}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <label className={styles.label} htmlFor="ai-language">{t("pages.aiQuestionGenerate.questionLanguage")}</label>
                                    <Select
                                        id="ai-language"
                                        className={styles.filterInput}
                                        value={language}
                                        onChange={(e) => setLanguage(e.target.value)}
                                        options={AI_LANGUAGE_OPTIONS.map((code) => ({
                                            value: code,
                                            label: t(`pages.aiQuestionGenerate.languages.${code}`),
                                        }))}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <label className={styles.label}>{t("pages.aiQuestionGenerate.questionCount")}</label>
                                    <div className={styles.stepper}>
                                        <button type="button" className={styles.stepperBtn}
                                                onClick={() => stepCount(-1)} disabled={numCount <= 1}
                                                aria-label={t("pages.aiQuestionGenerate.decrease")}>−
                                        </button>
                                        <input
                                            type="number"
                                            min={1}
                                            max={10}
                                            className={styles.stepperInput}
                                            value={count}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                if (raw === "") setCount("");
                                                else {
                                                    const n = parseInt(raw, 10);
                                                    if (!isNaN(n)) setCount(Math.min(10, Math.max(1, n)));
                                                }
                                            }}
                                            onBlur={() => {
                                                if (count === "" || count === undefined) setCount(1);
                                            }}
                                            aria-label={t("pages.aiQuestionGenerate.questionCount")}
                                        />
                                        <button type="button" className={styles.stepperBtn} onClick={() => stepCount(1)}
                                                disabled={numCount >= 10} aria-label={t("pages.aiQuestionGenerate.increase")}>+
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.field}>
                                    <label className={styles.label}>{t("pages.aiQuestionGenerate.optionCount")}</label>
                                    <div className={styles.stepper}>
                                        <button type="button" className={styles.stepperBtn}
                                                onClick={() => stepOptions(-1)} disabled={numOptionCount <= 2}
                                                aria-label={t("pages.aiQuestionGenerate.decrease")}>−
                                        </button>
                                        <input
                                            type="number"
                                            min={2}
                                            max={5}
                                            className={styles.stepperInput}
                                            value={optionCount}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                if (raw === "") setOptionCount("");
                                                else {
                                                    const n = parseInt(raw, 10);
                                                    if (!isNaN(n)) setOptionCount(Math.min(5, Math.max(2, n)));
                                                }
                                            }}
                                            onBlur={() => {
                                                if (optionCount === "" || optionCount === undefined) setOptionCount(2);
                                            }}
                                            aria-label={t("pages.aiQuestionGenerate.optionCount")}
                                        />
                                        <button type="button" className={styles.stepperBtn}
                                                onClick={() => stepOptions(1)} disabled={numOptionCount >= 5}
                                                aria-label={t("pages.aiQuestionGenerate.increase")}>+
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.formActions}>
                            <button type="button" className={styles.secondaryBtn} onClick={handleClose}
                                    disabled={loading || saving}>
                                {t("common.cancel")}
                            </button>
                            <button type="button" className={styles.primaryBtn} onClick={handleGenerate}
                                    disabled={loading || saving || !canGenerate}>
                                {loading ? (
                                    <>
                                        <span className={styles.btnSpinner} aria-hidden/>
                                        <span>{t("pages.aiQuestionGenerate.generating")}</span>
                                    </>
                                ) : (
                                    t("pages.aiQuestionGenerate.generate")
                                )}
                            </button>
                        </div>
                    </section>

                    <section ref={previewRef} className={styles.section} aria-labelledby="ai-questions-heading">
                        <div className={styles.sectionHeader}>
                            <h3 id="ai-questions-heading" className={styles.sectionTitle}>
                                {t("pages.aiQuestionGenerate.generatedQuestions")}
                            </h3>

                            {
                                !loading && generated.length > 0 &&
                                <button
                                    type="button"
                                    className={styles.regenerateBtn}
                                    onClick={handleGenerateNew}
                                    disabled={loading}
                                >
                                    {loading ? t("pages.aiQuestionGenerate.generating") : t("pages.aiQuestionGenerate.regenerate")}
                                </button>
                            }
                        </div>

                        <div className={styles.preview}>
                            {loading && (
                                <div className={styles.loadingState}>
                                    <div className={styles.loadingContent}>
                                        <div className={styles.loadingDots}>
                                            <span/><span/><span/>
                                        </div>
                                        <p className={styles.loadingTitle}>{t("pages.aiQuestionGenerate.loadingTitle")}</p>
                                        <p className={styles.loadingSub}>{t("pages.aiQuestionGenerate.loadingSub")}</p>
                                    </div>
                                    {process.env.PUBLIC_URL && (
                                        <img className={styles.loadingImg}
                                             src={process.env.PUBLIC_URL + "/sparkles_gif.gif"} alt="" aria-hidden/>
                                    )}
                                </div>
                            )}

                            {!loading && generated.length === 0 && (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyIconWrap}>
                                        <span className={styles.emptyIcon}>📝</span>
                                    </div>
                                    <p className={styles.emptyText}>{t("pages.aiQuestionGenerate.emptyHint")}</p>
                                </div>
                            )}

                            {!loading && generated.length > 0 && (
                                <>
                                    <div className={styles.previewToolbar}>
                                        <label className={styles.selectAll}>
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                ref={(el) => {
                                                    if (el) el.indeterminate = indeterminate;
                                                }}
                                                onChange={toggleAll}
                                            />
                                            <span>{t("pages.aiQuestionGenerate.selectAll")}</span>
                                        </label>
                                        <span
                                            className={styles.selectedBadge}>{selectedCount} / {generated.length}</span>
                                    </div>

                                    <ul className={styles.questionList}>
                                        {generated.map((q, i) => {
                                            const key = q.uuid || String(i);
                                            return (
                                                <li key={key} className={styles.questionCard}>
                                                    <label className={styles.questionCardLabel}>
                                                        <input
                                                            type="checkbox"
                                                            className={styles.questionCheckbox}
                                                            checked={!!selected[key]}
                                                            onChange={() => toggleOne(key)}
                                                        />

                                                        <span className={styles.questionNumber}>{i + 1}</span>

                                                        <div className={styles.questionText}>
                                                            <QuestionContent text={q.text} />
                                                        </div>
                                                    </label>

                                                    <ul className={styles.optionsList}>
                                                        {q.options?.map((o, idx) => (
                                                            <li
                                                                key={o.optionText + idx}
                                                                className={`${styles.optionItem} ${o.correct ? styles.optionCorrect : ""}`}
                                                            >
                                                                <span className={styles.optionLetter}>
                                                                  {String.fromCharCode(65 + idx)}
                                                                </span>

                                                                <div className={styles.optionText}>
                                                                    <QuestionContent text={o.optionText} />
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </li>
                                            );
                                        })}
                                    </ul>

                                    <div className={styles.footerBar}>
                                        <button
                                            type="button"
                                            className={styles.successBtn}
                                            onClick={handleSave}
                                            disabled={saving || loading || selectedCount === 0}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className={styles.btnSpinner} aria-hidden/>
                                                    <span>{t("pages.aiQuestionGenerate.adding")}</span>
                                                </>
                                            ) : (
                                                t(examBaseId ? "pages.aiQuestionGenerate.addToExam" : "pages.aiQuestionGenerate.addToBank", { count: selectedCount })
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default AiQuestionGenerateModal;
